import React, { Fragment } from "react";
import { Text, View } from "react-native";
import { Avatar } from "../ui/avatar";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import RemoteIcon from "@/assets/svgs/remote-icon";
import UserIcon from "@/assets/svgs/user-icon";
import ClockIcon from "@/assets/svgs/clock-icon";
import { Button } from "../ui/button";
import CloseIcon from "@/assets/svgs/close-icon";
import type { api } from "../utils/convex-api";

type BookingType =
  NonNullable<typeof api.parent.home.queries.pendingBookings._returnType>[0];

function ParentBookingCard({
  bookings,
  isRecurring,
  onCancelRequest,
  isCancellingBookingRequest,
}: {
  bookings: BookingType[];
  isRecurring: boolean;
  onCancelRequest: () => void;
  isCancellingBookingRequest: boolean;
}) {
  const fmt = useFormatDate();
  const firstBooking = bookings[0]!;
  const lastBooking = bookings[bookings.length - 1]!;

  const formatDateRange = () => {
    if (!isRecurring || bookings.length === 1) {
      return fmt(firstBooking.startTime, "do MMMM");
    }
    const sameMonth =
      fmt(firstBooking.startTime, "M") === fmt(lastBooking.startTime, "M");

    if (sameMonth) {
      return `${fmt(firstBooking.startTime, "do")} to ${fmt(lastBooking.startTime, "do MMMM")}`;
    }
    return `${fmt(firstBooking.startTime, "do MMM")} to ${fmt(lastBooking.startTime, "do MMM")}`;
  };

  return (
    <Fragment>
      <View
        key={firstBooking._id}
        className="border border-gray-200 p-4 rounded-[17px] gap-4"
      >
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-2">
            <Text
              className={`font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]`}
            >
              {firstBooking.subject}
            </Text>
          </View>
          <View className="items-end">
            <Text
              className={`font-geologica-medium font-medium text-base text-gray-800 leading-[1.2]`}
            >
              {formatDateRange()}
            </Text>
            {isRecurring && (
              <Text className="font-geologica-regular font-normal text-xs text-teal-600 leading-[1.5]">
                {bookings.length} recurring lessons
              </Text>
            )}
          </View>
        </View>

        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-2">
            <Avatar size="sm" className="h-6 w-6">
              <Avatar.Content
                label={firstBooking.tutor.firstName ?? ""}
                labelSize="sm"
                size="sm"
                source={
                  firstBooking.tutor.imageUrl
                    ? { uri: firstBooking.tutor.imageUrl }
                    : undefined
                }
              />
            </Avatar>
            <Text
              className={`font-geologica-regular font-normal ${"text-gray-800"} text-sm leading-[1.5]`}
            >
              {firstBooking.tutor.firstName}
            </Text>
          </View>

          <View className="flex-row items-center gap-[6px]">
            {firstBooking.type === "in_person" ? (
              <UserIcon className="size-4" pathClassName={"stroke-gray-800"} />
            ) : (
              <RemoteIcon
                className="size-4"
                pathClassName={"stroke-gray-800"}
              />
            )}
            <Text
              className={`font-geologica-regular font-normal ${"text-gray-800"} text-sm leading-[1.5]`}
            >
              {firstBooking.type === "in_person" ? "In person" : "Remote"}
            </Text>
          </View>

          <View className="flex-row items-center gap-2">
            <ClockIcon className="size-4" pathClassName={"stroke-gray-800"} />
            <Text
              className={`font-geologica-regular font-normal ${"text-gray-800"} text-sm leading-[1.5]`}
            >
              {fmt(firstBooking.startTime, "HH:mm")} -{" "}
              {fmt(firstBooking.endTime, "HH:mm")}
            </Text>
          </View>
        </View>

        <View className="flex-row items-center justify-end gap-2">
          <Button
            intent="primary"
            size="sm"
            icon
            className="border"
            onPress={onCancelRequest}
            outline
            disabled={isCancellingBookingRequest}
          >
            <Button.Icon
              as={CloseIcon}
              pathClassName="stroke-teachLoop-accentPrimary"
            />
            <Button.Text size="sm" intent="primary">
              Revoke request{isRecurring ? "s" : ""}
            </Button.Text>
          </Button>
        </View>
      </View>
    </Fragment>
  );
}

export default ParentBookingCard;
